
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  watch,
  Ref
} from "vue";
import { useRouter, RouteLocationNormalizedLoaded } from "vue-router";
import draggable from "vuedraggable";
import { useAuth, PermissionPayload } from "../../modules/auth";
import { useToast } from "../../modules/message";
import {
  OrcamentoPayload,
  OrcamentoFilterPayload,
  EstagioOrcamento,
  useOrcamentoService
} from "../../modules/business/orcamento/orcamentoBusiness";
import { ClientePayload } from "../../modules/business/cliente/clienteBusiness";
import { TransportadorPayload } from "../../modules/business/transportador/transportadorBusiness";
import EntitySelectorCliente from "../cliente/EntitySelectorCliente.vue";
import EntitySelectorTransportador from "../transportador/EntitySelectorTransportador.vue";
import CardOrcamento from "./CardOrcamento.vue";
import CadastroOrcamento from "./CadastroOrcamento.vue";

interface KanbanState {
  filter: OrcamentoFilterPayload;
  colunas: Record<string, Array<OrcamentoPayload>>;
  mostrarArquivados: boolean;
  stateReady: boolean;
  stateId: string;
  loadings: Array<Ref<boolean>>;
  showEdit: boolean;
  idOrcamentoEdit: number | null;
  keyRegister: string;
}

export default defineComponent({
  setup(props) {
    const router = useRouter();
    const orcamentoService = useOrcamentoService();

    const state = reactive<KanbanState>({
      filter: orcamentoService.safeFilterOrcamento(),
      colunas: {},
      mostrarArquivados: false,
      stateReady: false,
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2),
      loadings: [],
      showEdit: false,
      idOrcamentoEdit: null,
      keyRegister: ""
    });

    const stateLoading = computed(() => state.loadings.length !== 0);

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => useAuth().amIAllowed(authority, permissionInfo);

    const origens = orcamentoService.getOrigens();

    const estagiosVisiveis = computed<Array<EstagioOrcamento>>(() =>
      orcamentoService.getEstagios(state.mostrarArquivados)
    );

    const inicializarColunas = () => {
      const novas: Record<string, Array<OrcamentoPayload>> = {};
      orcamentoService.getEstagios(true).forEach((e) => {
        novas[e.codigo] = [];
      });
      state.colunas = novas;
    };

    const carregarPagina = (estagio: string, page: number): Promise<void> => {
      const { searchOrcamentos, data, loading } = useOrcamentoService();
      state.loadings.push(loading);
      const filterEstagio = { ...state.filter, estagio };
      return searchOrcamentos(filterEstagio, page, true)
        .then(() => {
          const resp = data.value as {
            content: Array<OrcamentoPayload>;
            page: { totalPages: number };
          };
          state.colunas[estagio].push(...(resp.content ?? []));
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          if (resp.page && page + 1 < resp.page.totalPages) {
            return carregarPagina(estagio, page + 1);
          }
          return undefined;
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    const recarregar = () => {
      inicializarColunas();
      const promessas: Array<Promise<unknown>> = [];
      orcamentoService.getEstagios(true).forEach((e) => {
        promessas.push(carregarPagina(e.codigo, 0));
      });
      return Promise.all(promessas);
    };

    const limparFiltros = () => {
      state.filter = orcamentoService.safeFilterOrcamento();
      recarregar();
    };

    const confirmSelectionCliente = (c?: ClientePayload) => {
      state.filter.idCliente = c && c.idCliente ? c.idCliente : null;
      recarregar();
    };

    const confirmSelectionTransportador = (t: TransportadorPayload | null) => {
      state.filter.idTransportador =
        t && t.idTransportador ? t.idTransportador : null;
      recarregar();
    };

    const onDragChange = (
      evt: { added?: { element: OrcamentoPayload; newIndex: number } },
      estagioDestino: string
    ) => {
      if (!evt.added) return;
      const orcamento = evt.added.element;
      const estagioOrigem = orcamento.estagio ?? "";

      if (estagioOrigem === estagioDestino) return;

      if (
        !orcamentoService.isTransicaoArrastavel(estagioOrigem, estagioDestino)
      ) {
        // revert: remover do destino, devolver à origem
        const idx = state.colunas[estagioDestino].findIndex(
          (o) => o.idOrcamento === orcamento.idOrcamento
        );
        if (idx >= 0) {
          state.colunas[estagioDestino].splice(idx, 1);
        }
        state.colunas[estagioOrigem].push(orcamento);

        const orientacao = orcamentoService.getOrientacaoFluxo(estagioOrigem);
        if (
          orientacao &&
          orcamentoService.isTransicaoPermitida(estagioOrigem, estagioDestino)
        ) {
          useToast().warning(orientacao);
        } else {
          useToast().error(
            `Transição inválida: ${estagioOrigem} → ${estagioDestino}`
          );
        }
        return;
      }

      const { updateEstagio, data, loading } = useOrcamentoService();
      state.loadings.push(loading);
      updateEstagio(orcamento.idOrcamento as number, {
        estagio: estagioDestino
      })
        .then(() => {
          const atualizado = data.value as OrcamentoPayload;
          const idx = state.colunas[estagioDestino].findIndex(
            (o) => o.idOrcamento === atualizado.idOrcamento
          );
          if (idx >= 0) {
            state.colunas[estagioDestino][idx] = atualizado;
          }
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        })
        .catch(() => {
          // revert em caso de erro
          const idx = state.colunas[estagioDestino].findIndex(
            (o) => o.idOrcamento === orcamento.idOrcamento
          );
          if (idx >= 0) {
            state.colunas[estagioDestino].splice(idx, 1);
          }
          state.colunas[estagioOrigem].push(orcamento);
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    const gerarKeyRegister = () => {
      state.keyRegister = state.idOrcamentoEdit
        ? state.idOrcamentoEdit + ""
        : "new-" +
          Math.random()
            .toString(36)
            .substring(2);
    };

    const abrirOrcamento = (orcamento: OrcamentoPayload) => {
      if (orcamento.idOrcamento) {
        router.push({
          name: "orcamento-edit",
          params: { idOrcamento: orcamento.idOrcamento + "" }
        });
      }
    };

    const novoOrcamento = () => {
      router.push({ name: "orcamento-new" });
    };

    const processViewRoute = (
      route: Partial<RouteLocationNormalizedLoaded>
    ) => {
      if (route.name === "orcamento-new") {
        state.idOrcamentoEdit = null;
        state.showEdit = true;
        gerarKeyRegister();
      } else if (route.name === "orcamento-edit") {
        const raw = route.params?.idOrcamento;
        state.idOrcamentoEdit = raw
          ? Array.isArray(raw)
            ? +raw[0]
            : +raw
          : null;
        state.showEdit = true;
        gerarKeyRegister();
      } else {
        state.showEdit = false;
        state.idOrcamentoEdit = null;
      }
    };

    const afterRegisterScreenClosed = () => {
      router.push({ name: "orcamento" });
      recarregar();
    };

    const afterSaveNewEntity = (idOrcamento: number) => {
      router.push({
        name: "orcamento-edit",
        params: { idOrcamento: idOrcamento + "" }
      });
    };

    onMounted(async () => {
      inicializarColunas();
      state.stateReady = true;
      watch([router.currentRoute], () => {
        processViewRoute(router.currentRoute.value);
      });
      processViewRoute(router.currentRoute.value);
      if (props.idOrcamento) {
        state.idOrcamentoEdit = props.idOrcamento;
        state.showEdit = true;
        gerarKeyRegister();
      } else if (props.isNew) {
        state.showEdit = true;
        gerarKeyRegister();
      }
      recarregar();
    });

    return {
      amIAllowed,
      origens,
      estagiosVisiveis,
      stateLoading,
      recarregar,
      limparFiltros,
      confirmSelectionCliente,
      confirmSelectionTransportador,
      onDragChange,
      abrirOrcamento,
      novoOrcamento,
      afterRegisterScreenClosed,
      afterSaveNewEntity,
      ...toRefs(state)
    };
  },
  components: {
    draggable,
    EntitySelectorCliente,
    EntitySelectorTransportador,
    CardOrcamento,
    CadastroOrcamento
  },
  props: {
    idOrcamento: Number,
    isNew: Boolean
  }
});
